import React, { useMemo, useState } from "react";
import { SearchInput } from "../../shared/components/table/components/TempFilter";
import { useTranslation } from "react-i18next";
import ReportCard from "./ReportCard";
import { putApi } from "../../api/getUser";
import { Spinner } from "react-bootstrap";
import TableModal from "./TableModal";
import { DeletePopup } from "../../shared/components/table/components/DeletePopup";
import { HintText } from "../../shared/components/form/FormElements";
import styled from "styled-components";
import { textAlign } from "../../utils/directions";

function SavedReports({
  savedReports,
  onEditReport,
  fetchReports,
  fetchReportsLoading,
}) {
  const { t } = useTranslation("common");

  const [search, setSearch] = useState("");

  const filteredReports = useMemo(() => {
    return savedReports.filter((report) =>
      report.report_name.toLowerCase().includes(search.trim().toLowerCase())
    );
  }, [search, savedReports]);

  //States for table modal
  const [tableData, setTableData] = useState(null);
  //If tableData has > 1000 rows
  const [sqlQuery, setSqlQuery] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);

  const [showDeletePopup, setShowDeletePopup] = useState(false);

  const handleDeleteClick = (report) => {
    setSelectedReport(report);
    setShowDeletePopup(true);
  };

  const runReport = async (report) => {
    setModalLoading(true);
    setSelectedReport(report);
    try {
      const response = await putApi(
        {
          querystring: "run_ai_report",
          report_id: report.report_id,
        },
        "dashboard"
      );

      if (response?.success) {
        setTableData(response.data);
        setHasMore(response?.has_more);
        setSqlQuery(response?.sql_query);
        setIsModalOpen(true);
      }
    } catch (error) {
      console.error("Failed to run report:", error);
    } finally {
      setModalLoading(false);
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setTableData(null);
    setHasMore(false);
    setSqlQuery(null);
    setSelectedReport(null);
    // Refresh the saved reports list to update the last_run field after modal is closed
    fetchReports();
  };

  return (
    <div className="container py-4 h-100" style={{ background: "#f9fafb" }}>
      {fetchReportsLoading ? (
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ height: "calc(100vh - 320px)" }}
        >
          <Spinner animation="border" variant="info" />
        </div>
      ) : (
        <>
          <div className="d-flex align-items-center mb-2 gap-2">
            {/* <div className="position-relative"> */}
            <SearchInput
              type="text"
              placeholder={t("search_saved_reports")}
              style={{ width: "16rem", height: "2.1rem" }}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              disabled={!savedReports.length}
            />
            {/* </div> */}
            <span
              style={{
                background: "#9FC5F8",
                border: `1px solid #9FC5F8`,
                borderRadius: "25px",
                width: "min-content",
                whiteSpace: "nowrap",
                padding: "0.25rem 0.5rem",
                fontSize: "12px",
              }}
            >
              {`${filteredReports.length} ${t("reports_sm", {
                count: filteredReports.length,
              })}`}
            </span>
          </div>

          <HintTextRTL className="mt-3 pt-0">{`💡 ${t(
            "running_reports_free_msg"
          )}`}</HintTextRTL>

          {!filteredReports.length && (
            <div
              style={{ height: "calc(100vh - 360px)" }}
              className="d-flex align-items-center justify-content-center"
            >
              <NoRecordsText>{t("no_records")}</NoRecordsText>
            </div>
          )}

          <div
            className="row g-3"
            style={{
              maxHeight: "calc(100vh - 282px)",
              overflow: "auto",
              marginTop: "0.2rem",
            }}
          >
            {filteredReports.map((report) => {
              return (
                <ReportCard
                  key={report.report_id}
                  report={report}
                  onEditReport={onEditReport}
                  onRunReport={runReport}
                  isRunning={
                    modalLoading &&
                    selectedReport?.report_id === report.report_id
                  }
                  onDelete={handleDeleteClick}
                />
              );
            })}
          </div>
          {isModalOpen && (
            <TableModal
              showModal={isModalOpen}
              data={tableData}
              onClose={closeModal}
              hasMore={hasMore}
              sqlQuery={sqlQuery}
              //title={selectedReport?.report_name}
            />
          )}
          {showDeletePopup && (
            <DeletePopup
              showPopup={showDeletePopup}
              setShowPopup={setShowDeletePopup}
              deleteApiData={{
                delete_ids: selectedReport.report_id,
                querystring: "multiple_delete",
                sub_query_string: "AI_REPORTS",
              }}
              screen={"reporting"}
              onDeleteSuccess={fetchReports}
            />
          )}
        </>
      )}
    </div>
  );
}

const HintTextRTL = styled(HintText)`
  text-align: ${textAlign};
`;

const NoRecordsText = styled.p`
  font-size: 16px;
  text-align: ${textAlign};
`;

export default SavedReports;
